import { useMemo, useState, type ImgHTMLAttributes } from "react";
import dimensions from "@/data/imageDimensions.json";
import { publicAsset, publicSrcSet } from "@/lib/publicAssets";
import { catalogImage, catalogImageSet, shopifyImage, shopifyImageSet } from "@/lib/imageSources";

const knownDimensions: Record<string, number[]> = dimensions;

/** Native img markup, with intrinsic dimensions; no wrapper or CSS changes. */
export default function SiteImage({ src, srcSet, sizes, style, width, height, className = "", loading = "lazy", decoding = "async", onLoad, ...props }: ImgHTMLAttributes<HTMLImageElement>) {
  const [discovered, setDiscovered] = useState<{ src: string | undefined; size: [number, number] } | null>(null);
  const key = src?.startsWith("http") ? src : `/${src?.replace(/^\/+/, "") ?? ""}`;
  const catalog = catalogImage(src);
  const intrinsic = knownDimensions[key] ?? (catalog ? [catalog.width, catalog.height] : null)
    ?? (discovered?.src === src ? discovered?.size : null);
  // A new width attribute must not override the old automatic width of an
  // image whose only CSS constraint is its height (notably the site logo).
  const heightOnly = /(?:^|\s)(?:max-)?h-/.test(className) && !/(?:^|\s)w-/.test(className);
  const logo = src?.endsWith("/reframe-logo.png") && import.meta.env.PROD;
  const remote = useMemo(() => ({ src: shopifyImage(src, 1280), srcSet: shopifyImageSet(src) }), [src]);
  const image = (
    <img
      {...props}
      src={publicAsset(logo ? "/generated-media/reframe-logo-348.png" : remote.src)}
      srcSet={publicSrcSet(logo ? undefined : srcSet ?? remote.srcSet)}
      sizes={sizes ?? (remote.srcSet ? "(min-width: 1024px) 600px, 100vw" : undefined)}
      style={style}
      className={heightOnly ? `${className} w-auto` : className}
      width={width ?? intrinsic?.[0]}
      height={height ?? intrinsic?.[1]}
      loading={loading}
      decoding={decoding}
      onLoad={(event) => {
        if (!intrinsic && event.currentTarget.naturalWidth) {
          setDiscovered({ src, size: [event.currentTarget.naturalWidth, event.currentTarget.naturalHeight] });
        }
        onLoad?.(event);
      }}
    />
  );
  return logo ? <picture style={{ display: "contents" }}>
    <source type="image/webp" srcSet={publicSrcSet("/generated-media/reframe-logo-174.webp 1x, /generated-media/reframe-logo-348.webp 2x")} />
    {image}
  </picture> : catalog ? <picture style={{ display: "contents" }}>
    <source type="image/avif" srcSet={catalogImageSet(src, "avif")}
      sizes={sizes ?? "(min-width: 1024px) 600px, 100vw"} />
    {image}
  </picture> : image;
}
